import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import {
	BillingAddonType,
	BillingCycle,
	BillingFeature,
	PlanExtrasType,
	PlanFeatureItem,
	PlanType,
	type BillingServiceType,
} from 'webapp/enums/enums';
import { getFormattedPrice, getFormattedSyncInterval } from 'webapp/utils/number';

import ActiveFeatures from 'webapp/models-custom/active-features';
import Serviced from 'webapp/models-custom/serviced';
import { ITEM_ID_EXTRA_DS } from 'webapp/models-custom/subscription';

export interface PlanData {
	id: string;
	productId: string; // To differentiate between agency and non-agency plan (without billing cycle). e.g. 'performer', 'agency-performer'
	name: string;
	description: string;
	billingCycle: BillingCycle;
	price: number;
	featureSet: string;
	succeedBy: string;
	isLegacy: boolean;
	isTrialAvailable: boolean;
	extras: {
		type: PlanExtrasType;
		id: string;
		price: number;
		canAddOnTrial: boolean;
		productPrice: string;
		freeAmount?: number;
	}[];
	planOnReactivate: string;
	allowCycleSwitch: boolean;
	isAgency?: boolean;
	isEnterprise?: boolean;
	version: string | null;
}

export const VERSION_ID_SEPT_2026 = 'september_2026';

export type FeatureSetData = Record<string, number | null>;

interface AvailableAddonInfo {
	id: BillingAddonType;
	price: number;
	canAddOnTrial: boolean;
	productPrice: string;
}

interface ServiceInfo {
	id: BillingServiceType;
	productPrice: string;
	price: number;
}

export interface NumericAndFormattedPrice {
	price: number;
	priceFormatted: string;
}

// Value rendered for a single PlanFeatureItem in the plan comparison table.
// At least one field is set; flag + text may be combined.
export interface PlanFeatureItemValue {
	text?: string;
	flag?: boolean;
	isAddon?: boolean;
	tooltip?: string;
}

export default class BillingPlan extends Serviced {
	@service('billing') declare billingService: Service.Billing;

	id: string;
	productId: string;
	name: string;
	description: string;
	billingCycle: BillingCycle;
	price: number;
	featureSet: string;
	isAgency: boolean;
	isEnterprise: boolean; // if plan is also available on isAgency (enterprise flag) spaces. false would mean we hide it for them. currently true on all except analyst
	isLegacy: boolean;
	allowCycleSwitch: boolean; // means we show all different billingcycles from the same plan (even if legacy) (this prop is the same for all cycles of the same plan)
	isTrialAvailable: boolean;
	succeedBy: string; // Do not use this field as an indicator if this plan is legacy (pre 2023 one) or new. For example the new 2023 Performer plan also has succeededBy (another 2023 plan)
	version: string;

	featureData: FeatureSetData;
	availableAddonsAndTopUps: AvailableAddonInfo[];
	serviceInfo: ServiceInfo[];
	sourcePrice: number;
	freeSources: number;

	features!: ActiveFeatures;

	constructor(planData: PlanData, featureSetData: FeatureSetData) {
		super();

		this.id = planData.id;
		this.productId = planData.productId;
		this.name = planData.name;
		this.description = planData.description;
		this.billingCycle = planData.billingCycle;
		this.price = planData.price;
		this.featureSet = planData.featureSet;
		this.isAgency = !!planData.isAgency;
		this.isEnterprise = !!planData.isEnterprise;
		this.isLegacy = !!planData.isLegacy;
		this.allowCycleSwitch = !!planData.allowCycleSwitch;
		this.isTrialAvailable = !!planData.isTrialAvailable;
		this.succeedBy = planData.succeedBy;
		this.version = planData.version || '';

		this.featureData = featureSetData;
		this.features = new ActiveFeatures();
		this.features.updateFromPlan(this);

		this.availableAddonsAndTopUps = planData.extras
			.filter((item) => [PlanExtrasType.ADD_ON, PlanExtrasType.TOP_UP].includes(item.type))
			.map((item) => {
				return {
					id: item.id as BillingAddonType,
					price: item.price,
					canAddOnTrial: item.canAddOnTrial,
					productPrice: item.productPrice,
				};
			});

		this.serviceInfo = planData.extras
			.filter((item) => item.type === PlanExtrasType.ONE_TIME_SERVICE)
			.map((item) => {
				return {
					id: item.id as BillingServiceType,
					productPrice: item.productPrice,
					price: item.price,
				};
			});

		const sourceExtra = planData.extras.find((item) => item.id === ITEM_ID_EXTRA_DS);
		this.sourcePrice = sourceExtra?.price || 0;
		this.freeSources = sourceExtra?.freeAmount || 0;
	}

	get freeSourcesFormatted() {
		return this.freeSources === null ? 'Unlimited' : this.freeSources;
	}

	get isVIP(): boolean {
		return this.id === PlanType.VIP;
	}

	get isFree(): boolean {
		return String(this.id).startsWith(PlanType.FREE);
	}

	get isZeroPayment(): boolean {
		return !this.price;
	}

	// ------- plan type -------

	get planType(): PlanType {
		const planType = this.featureSet
			.replace('_2023', '')
			.replace('_2024', '')
			.replace('_2025', '')
			.replace('_2026', '')
			.replace('agency_', '');
		return planType as PlanType;
	}

	get isAnalyst(): boolean {
		return this.planType === PlanType.ANALYST;
	}

	get isStarter() {
		return this.planType === PlanType.STARTER;
	}

	get isProfessional() {
		return this.planType === PlanType.PROFESSIONAL;
	}

	get isPerformer() {
		return this.planType === PlanType.PERFORMER;
	}

	get isGrowth() {
		return this.planType === PlanType.GROWTH;
	}

	get isPremium() {
		return this.planType === PlanType.PREMIUM;
	}

	get isCustom(): boolean {
		return this.planType === PlanType.CUSTOM;
	}

	get is2024Plan() {
		return this.id.includes('_2024');
	}

	get is2026SeptPlan() {
		return this.version === VERSION_ID_SEPT_2026;
	}

	get isTeamCore() {
		return this.planType === PlanType.TEAM_CORE;
	}

	get isTeamScale() {
		return this.planType === PlanType.TEAM_SCALE;
	}

	get isExtraSourcesAllowedOnPlan() {
		if (this.is2026SeptPlan) {
			return false;
		}
		return !this.isCustom && !this.isFree && !this.isAnalyst;
	}

	// ------- billing cycle -------

	get isMonthly(): boolean {
		return this.billingCycle === BillingCycle.MONTHLY;
	}

	get isQuarterly(): boolean {
		return this.billingCycle === BillingCycle.QUARTERLY;
	}

	get isYearly(): boolean {
		return this.billingCycle === BillingCycle.YEARLY;
	}

	get monthsInCycle(): number {
		return this.isYearly ? 12 : this.isQuarterly ? 3 : 1;
	}

	get billingCycleText(): string {
		return this.isYearly ? 'year' : this.isQuarterly ? 'quarter' : 'month';
	}

	get billingCycleTextLong(): string {
		return this.billingCycleText + 'ly';
	}

	get billingCycleTextShort(): string {
		return this.isYearly ? 'y' : this.isQuarterly ? 'qt' : 'mo';
	}

	// -------- prices -------

	get priceFormatted(): string {
		return getFormattedPrice(this.price, 2);
	}

	get sourcePriceFormatted(): string {
		return getFormattedPrice(this.sourcePrice, 2);
	}

	getDiscount(monthlyDiscount = 0): number {
		// use monthly discount, and "discount" for the discount (quarterly -10%, yearly -20%)
		let discountShare = 1;
		if (this.isQuarterly) {
			discountShare = 0.9;
		} else if (this.isYearly) {
			discountShare = 0.8;
		}
		return monthlyDiscount * this.monthsInCycle * discountShare;
	}

	getPriceWithSources(sourceCount: number): number {
		return this.sourcePrice * sourceCount;
	}

	getPriceWithSourcesFormatted(sourceCount: number, decimals = 2): string {
		const price = this.getPriceWithSources(sourceCount);
		return getFormattedPrice(price, decimals);
	}

	getTotalPriceWithSources(sourceCount: number): number {
		const sourcePrice = this.getPriceWithSources(sourceCount),
			baseFee = this.price;
		return sourcePrice + baseFee;
	}

	getNumericAndFormattedTotalPriceWithSources(
		sourceCount: number,
		decimals = 2,
	): NumericAndFormattedPrice {
		const price = this.getTotalPriceWithSources(sourceCount);

		return {
			price,
			priceFormatted: getFormattedPrice(price, decimals),
		};
	}

	getTotalPriceWithSourcesAndDiscount(sourceCount: number, monthlyDiscount = 0): number {
		let price = this.getTotalPriceWithSources(sourceCount);
		price -= this.getDiscount(monthlyDiscount);
		if (price < 0) {
			price = 0;
		}
		return price;
	}

	getNumericAndFormattedTotalPriceWithSourcesAndDiscount(
		sourceCount: number,
		monthlyDiscount = 0,
		decimals = 2,
	): NumericAndFormattedPrice {
		const price = this.getTotalPriceWithSourcesAndDiscount(sourceCount, monthlyDiscount);

		return {
			price,
			priceFormatted: getFormattedPrice(price, decimals),
		};
	}

	// -------- features ---------

	getFeatureValue(featureName: BillingFeature): number | boolean | null {
		return this.features.getValue(featureName);
	}

	// Returns the value shown for a single feature-comparison item on this plan.
	// The comparison table (and other places) declare which items to show and in
	// what order; this method owns the per-plan value logic for each item.
	getPlanFeatureItemValue(item: PlanFeatureItem): PlanFeatureItemValue {
		switch (item) {
			case PlanFeatureItem.DATA_SOURCES: {
				if (this.isCustom) {
					return { text: 'Custom' };
				}
				const max = this.features.sourcesMax ?? '∞';
				if (this.billingService.is2026SeptBillingGroup) {
					return { text: String(max) };
				} else {
					return { text: `${this.freeSources} / ${max}` };
				}
			}
			case PlanFeatureItem.GOVERNED_SEMANTIC_LAYER:
			case PlanFeatureItem.DATA_LINEAGE:
			case PlanFeatureItem.CLOUD_INTEGRATIONS:
				return { flag: true };
			case PlanFeatureItem.CUSTOM_INTEGRATIONS:
				return {
					flag:
						this.hasFeatureEnabled(BillingFeature.SHEET_SOURCES) &&
						this.hasFeatureEnabled(BillingFeature.SQL_SOURCES) &&
						this.hasFeatureEnabled(BillingFeature.WORKFLOW_INTEGRATIONS),
				};
			case PlanFeatureItem.DATASETS: {
				const fullDatasets = this.hasFeatureEnabled(BillingFeature.ADVANCED_DATA_SOURCES);
				if (
					!fullDatasets &&
					this.hasFeatureEnabled(BillingFeature.ADVANCED_DATA_SOURCES_LIMITED)
				) {
					return { flag: true, text: 'Limited*' };
				}
				return { flag: fullDatasets };
			}
			case PlanFeatureItem.DATABOX_API:
				return { flag: this.hasFeatureEnabled(BillingFeature.INGESTION_API) };
			case PlanFeatureItem.HISTORICAL_DATA:
				return {
					text: this.features.formatHistoricalDataValue(
						this.getFeatureValue(BillingFeature.HISTORICAL_DATA),
						true,
					),
				};
			case PlanFeatureItem.MAX_SYNC_FREQUENCY: {
				const hasPrioritySyncAsPartOfPlan =
					!!this.features.prioritySyncSourceCount || this.isCustom;
				return {
					text: hasPrioritySyncAsPartOfPlan
						? '15min'
						: getFormattedSyncInterval(this.features.dataRefreshInterval, true),
					tooltip: hasPrioritySyncAsPartOfPlan
						? 'The default sync interval for your data sources, adjustable per source. Actual frequencies may vary based on your connected data providers.'
						: '',
				};
			}
			case PlanFeatureItem.AI_ANALYST:
				return { flag: this.hasFeatureEnabled(BillingFeature.AI_CHAT_ASSISTANT) };
			case PlanFeatureItem.AI_CREDITS: {
				if (this.isCustom) {
					return { text: 'Custom' };
				}
				const max = this.features.aiCreditsMax;
				let text = max === null ? '∞' : String(max);
				if (this.isFree) {
					text += ' (fixed)';
				}
				return { text };
			}
			case PlanFeatureItem.PERFORMANCE_SUMMARIES:
				return { flag: this.hasFeatureEnabled(BillingFeature.GENERATIVE_AI) };
			case PlanFeatureItem.MCP_SERVER:
				return { flag: this.hasFeatureEnabled(BillingFeature.MCP) };
			case PlanFeatureItem.PREBUILT_METRICS_TEMPLATES:
				return { flag: true };
			case PlanFeatureItem.ANOMALY_DETECTION:
				return { flag: this.hasFeatureEnabled(BillingFeature.ANOMALY_DETECTION) };
			case PlanFeatureItem.DASHBOARDS_REPORTS: {
				// theoretically, boards and reports limitation should be the same, when set at all
				const maxBoards = this.features.boardsMax,
					maxReports = this.features.reportsMax;
				return {
					text:
						maxBoards === null && maxReports === null
							? '∞'
							: String(Math.min(maxBoards || 0, maxReports || 0)),
				};
			}
			case PlanFeatureItem.CUSTOM_METRICS: {
				const max = this.features.customMetricsMax;
				return { text: max === null ? '∞' : String(max) };
			}
			case PlanFeatureItem.CSV_EXPORT:
			case PlanFeatureItem.SHARING:
				return { flag: this.hasFeatureEnabled(BillingFeature.SHARING) };
			case PlanFeatureItem.ACCOUNT_TEMPLATES:
				return { flag: this.hasFeatureEnabled(BillingFeature.ACCOUNT_TEMPLATES) };
			case PlanFeatureItem.LOOPED_DATABOARDS:
				return { flag: this.hasFeatureEnabled(BillingFeature.BOARD_LOOPS) };
			case PlanFeatureItem.REPORT_SLIDE_LIBRARY:
				return { flag: this.hasFeatureEnabled(BillingFeature.ADVANCED_REPORTS) };
			case PlanFeatureItem.SSO:
				return this.getFeatureOrAddonValue(BillingFeature.SSO);
			case PlanFeatureItem.GOALS:
				return { flag: this.hasFeatureEnabled(BillingFeature.GOALS) };
			case PlanFeatureItem.OKRS:
				return this.getFeatureOrAddonValue(BillingFeature.OKR);
			case PlanFeatureItem.USERS: {
				const max = this.features.usersMax;
				return { text: max === null ? '∞' : String(max) };
			}
			case PlanFeatureItem.FISCAL_CALENDAR:
				return this.getFeatureOrAddonValue(BillingFeature.FISCAL_CALENDARS);
			case PlanFeatureItem.WHITE_LABELING:
				return this.getFeatureOrAddonValue(BillingFeature.WHITE_LABELING);
			case PlanFeatureItem.SUB_ACCOUNTS:
				return this.getFeatureOrAddonValue(BillingFeature.CLIENTS);
			case PlanFeatureItem.CHAT_EMAIL_SUPPORT:
				return { flag: !this.isFree };
			case PlanFeatureItem.CUSTOM_DATE_RANGES:
				return { flag: this.hasFeatureEnabled(BillingFeature.CUSTOM_RANGE) };
			case PlanFeatureItem.MEDIA_CREATIVES:
				return { flag: this.hasFeatureEnabled(BillingFeature.MEDIA_CREATIVES) };
			case PlanFeatureItem.SMART_ALERTS:
				return { flag: this.hasFeatureEnabled(BillingFeature.SMART_ALERTS) };
			case PlanFeatureItem.DATA_STORIES:
				return { flag: this.hasFeatureEnabled(BillingFeature.GENERATIVE_AI) };
			case PlanFeatureItem.DATASET_TRANSFORMATIONS:
				return {
					flag: this.hasFeatureEnabled(BillingFeature.ADVANCED_DATASET_FUNCTIONALITIES),
				};
			case PlanFeatureItem.VERIFY_BADGE_ASSETS:
				return { flag: this.hasFeatureEnabled(BillingFeature.GOVERNANCE_VERIFICATION) };
			case PlanFeatureItem.ARTIFACTS:
				return { flag: this.hasFeatureEnabled(BillingFeature.AI_ARTIFACTS) };
			case PlanFeatureItem.PREBUILT_SKILLS:
				return { flag: this.hasFeatureEnabled(BillingFeature.AI_SKILLS_MARKETPLACE) };
			case PlanFeatureItem.CUSTOM_SKILLS:
				return { flag: this.hasFeatureEnabled(BillingFeature.AI_SKILLS_CUSTOM) };
			case PlanFeatureItem.GENIE_IN_SLACK:
				return { flag: this.hasFeatureEnabled(BillingFeature.AI_SLACK_ASSISTANT) };
			case PlanFeatureItem.ROUTINES:
				return { flag: this.hasFeatureEnabled(BillingFeature.AI_ROUTINES) };
			case PlanFeatureItem.AGENTS:
				return { flag: this.hasFeatureEnabled(BillingFeature.AI_AGENTS) };
			case PlanFeatureItem.MCP_CONNECTORS:
				return { flag: this.hasFeatureEnabled(BillingFeature.AI_MCP_CONNECTORS) };
			case PlanFeatureItem.UNBRANDED_DASHBOARDS:
				return { flag: this.hasFeatureEnabled(BillingFeature.SKIP_DW_BRANDING) };

			// custom-only features
			case PlanFeatureItem.SECURITY_COMPLIANCE_REVIEW:
			case PlanFeatureItem.AI_IMPLEMENTATION:
			case PlanFeatureItem.ENABLEMENT:
			case PlanFeatureItem.ACCOUNT_SETUP:
			case PlanFeatureItem.DATA_SEMANTIC_LAYER_SETUP:
				return { flag: this.isCustom };

			default: {
				const _exhaustive: never = item;
				return _exhaustive;
			}
		}
	}

	// Feature is either included in the plan, available as a paid add-on, or absent.
	private getFeatureOrAddonValue(feature: BillingFeature): PlanFeatureItemValue {
		const featureIncluded = this.hasFeatureEnabled(feature);
		const addonAvailable =
			!featureIncluded &&
			!!this.billingService.getAvailableAddonForFeatureOnPlan(this, feature);
		return addonAvailable ? { isAddon: true } : { flag: featureIncluded };
	}

	hasFeatureEnabled(feature: BillingFeature): boolean {
		const featureValue = this.getFeatureValue(feature);
		const isCountable = this.features.isCountable(feature);
		// for countable features: null means unlimited, >0 means has limit
		// for non-countable features: truthy means enabled
		return isCountable ? featureValue === null || (featureValue as number) > 0 : !!featureValue;
	}

	getAddonPrice(addonId: BillingAddonType): number | undefined {
		return this.findAvailableAddon(addonId)?.price;
	}

	getAddonProductPrice(addonId: BillingAddonType): string | undefined {
		return this.findAvailableAddon(addonId)?.productPrice;
	}

	getCanAddOnTrial(addonId: BillingAddonType): boolean | undefined {
		return this.findAvailableAddon(addonId)?.canAddOnTrial;
	}

	getServicePrice(serviceId: BillingServiceType): number | undefined {
		return this.serviceInfo.find((item) => item.id === serviceId)?.price;
	}

	getServiceProductPrice(serviceId: BillingServiceType): string | undefined {
		return this.serviceInfo.find((item) => item.id === serviceId)?.productPrice;
	}

	isAddonAvailable(addonId: BillingAddonType) {
		return !!this.findAvailableAddon(addonId);
	}

	private findAvailableAddon(addonId?: BillingAddonType) {
		return addonId
			? this.availableAddonsAndTopUps.find((item) => item.id === addonId)
			: undefined;
	}

	/*
		Plan List and successor mapping

		[
			{ featureSet: 'free', succeedBy: 'free_2023' },
			{ featureSet: 'free_2023', succeedBy: null },

			{ featureSet: 'starter', succeedBy: 'starter_2023' },
			{ featureSet: 'starter_2023', succeedBy: null },

			{ featureSet: 'professional', succeedBy: 'professional_2023' },
			{ featureSet: 'professional_2023', succeedBy: null },

			{ featureSet: 'performer', succeedBy: 'performer_2023' },
			{ featureSet: 'performer_2023', succeedBy: 'professional_2023' }, // !!! 2023 plan with successor

			{ featureSet: 'growth_2023', succeedBy: null },

			{ featureSet: 'premium_2023', succeedBy: null }
		]
	*/

	// Used to determine upgrade / downgrade
	// Users will always see either 'professional' or 'performer', never both
	// Same goes for Analyst (non-agency) vs Starter (agency), unless ofc legacy
	static planTypes = [
		PlanType.FREE,
		PlanType.ANALYST,
		PlanType.STARTER, // pre-september_2026
		PlanType.PROFESSIONAL, // pre-september_2026
		PlanType.PERFORMER, // pre-september_2026
		PlanType.GROWTH, // pre-september_2026
		PlanType.PREMIUM, // pre-september_2026
		PlanType.TEAM_CORE,
		PlanType.TEAM_SCALE,
		PlanType.CUSTOM,
	];
}
